import { format, isToday, isYesterday } from 'date-fns';
import { useEffect, useState } from 'react';
import { Drawer, DrawerContent } from '../../../../design-system/drawer/drawer';
import { Stack } from '../../../../design-system/stack/stack';
import { CategoryView, TransactionView } from '../../../../types/entity-types';
import { getAmountWithCurrency } from '../../../../utils/get-amount-with-currency';
import { getNormalizedAmount } from '../../../../utils/get-normalized-amount';
import { EvidenceWrapper } from './evidence/evidence-wrapper';
import { Header } from './header/header';
import { PaymentInformation } from './payment-information/payment-information';
import { createBeneficiaryDetails } from './utils/beneficiary-utils';
import { getTransactionSummaryMetrics } from './utils/get-transaction-summary-metrics';
import { getAdjacentTransactionsFromFullList, handleTransactionKeyboardNavigation } from './utils/navigation';
import { VendorSection } from './vendor-section/vendor-section';

type TransactionDetailsDrawerProps = {
  isOpen: boolean;
  transaction: TransactionView | null;
  transactions: TransactionView[];
  categories: CategoryView[];
  isEditingVendorModalOpen: boolean;
  onClose: () => void;
  onChangeVendor: () => void;
  onEditVendor: () => void;
  onChangeCategory: (categoryId: string) => void;
  onViewEvidence: (documentId: string) => void;
  onDownloadEvidence: (documentId: string) => void;
};

export const TransactionDetailsDrawer = ({
  isOpen,
  transaction,
  transactions,
  categories,
  isEditingVendorModalOpen,
  onClose,
  onChangeVendor,
  onEditVendor,
  onChangeCategory,
  onViewEvidence,
  onDownloadEvidence,
}: TransactionDetailsDrawerProps) => {
  const { prev, next } = transaction
    ? getAdjacentTransactionsFromFullList(transactions, transaction.id)
    : { prev: undefined, next: undefined };

  useEffect(() => {
    if (isEditingVendorModalOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      handleTransactionKeyboardNavigation(event, isOpen, prev, next);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, prev, next, isEditingVendorModalOpen]);

  const [vendorDescription, setVendorDescription] = useState<string>('');

  useEffect(() => {
    if (transaction?.vendor) {
      setVendorDescription(transaction.vendor.description ?? '');
    } else {
      setVendorDescription(
        transaction?.payee?.iban
          ? transaction.payee.iban
          : `${transaction?.payee?.accountNumber ?? ''} • ${transaction?.payee?.sortCode ?? ''}`,
      );
    }
  }, [transaction?.vendor, transaction?.payee]);

  if (!transaction) {
    return null;
  }

  const {
    amount,
    currency,
    isCredit,
    fspDate,
    vendor,
    baseCurrency,
    baseCurrencyAmount,
    status,
    description,
    reference,
    type,
    account,
    payee,
    evidence,
  } = transaction;

  const vendorName = vendor?.name ?? payee?.name ?? '';
  const vendorLogo = vendor?.logo ?? '';

  const normalizedAmount = getNormalizedAmount({ amount, isCredit });
  const amountWithCurrency = getAmountWithCurrency({ amount: normalizedAmount, currency });

  const normalizedBaseCurrencyAmount = getNormalizedAmount({ amount: baseCurrencyAmount, isCredit });
  const amountWithBaseCurrency = getAmountWithCurrency({
    amount: normalizedBaseCurrencyAmount,
    currency: baseCurrency,
  });

  const beneficiary = createBeneficiaryDetails(transaction);

  const formattedDate = isToday(fspDate)
    ? `Today at ${format(fspDate, 'HH:mm')}`
    : isYesterday(fspDate)
      ? `Yesterday at ${format(fspDate, 'HH:mm')}`
      : format(fspDate, "d MMM yyyy 'at' HH:mm");

  const vendorTransactions = transactions.filter((t) => t.vendor?.id === vendor?.id);
  const metrics = getTransactionSummaryMetrics(vendorTransactions, currency);

  return (
    <Drawer open={isOpen} onOpenChange={onClose} direction="right" modal={false}>
      <DrawerContent aria-describedby={undefined}>
        <Header
          vendor={{ name: vendorName, logo: vendorLogo }}
          amount={amountWithCurrency}
          amountWithBaseCurrency={amountWithBaseCurrency}
          isCredit={isCredit}
          date={formattedDate}
          prev={prev}
          next={next}
        />
        <Stack
          space={8}
          className="scrollbar-hide overflow-y-auto p-[18px] [&::-webkit-scrollbar]:hidden"
          align="stretch"
        >
          <PaymentInformation
            type={type}
            status={status ?? ''}
            reference={reference ?? description}
            transactionId={transaction.id}
            cardNumber={account?.accountInformation?.accountNo ?? ''}
            account={{
              name: account?.institution?.name,
              imageUrl: account?.institution?.logo,
              currency: account?.currency,
            }}
          />
          <VendorSection
            isCredit={isCredit}
            beneficiary={beneficiary}
            vendorLogo={vendorLogo}
            vendorName={vendorName}
            vendorDescription={vendorDescription}
            onEditVendor={onEditVendor}
            onChangeVendor={onChangeVendor}
            onChangeCategory={onChangeCategory}
            category={vendor?.category}
            categories={categories}
            transactionSummaryMetrics={metrics}
          />
          {evidence && evidence.length > 0 && (
            <EvidenceWrapper
              evidence={evidence}
              onViewEvidence={onViewEvidence}
              onDownloadEvidence={onDownloadEvidence}
            />
          )}
        </Stack>
      </DrawerContent>
    </Drawer>
  );
};
